/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ContextProps } from 'bundles/enterprise-admin-home/components/ContextButton';
import { AdminOrgSwitcherContent } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/AdminOrgSwitcherContent';
import { getFocusableElement } from 'bundles/enterprise-admin-navigation/utils/focusUtils';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  popup: css`
    position: absolute;
    bottom: 100%;
    left: 10px;
    width: 360px;
    max-height: min(70vh, 560px);
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-200, 16px);
    box-shadow: var(--cds-elevation-level3);
    z-index: 999;
    padding: var(--cds-spacing-100);
  `,
  popupHidden: css`
    display: none;
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  thirdPartyOrganization: AdminDashboardOrg;
  contexts: ContextProps[];
  /**
   * Element that toggles this popup. Clicks on this element are treated as "inside"
   * so the toggle handler can manage open/close state without the outside-click
   * handler racing to close first (which would cause an immediate reopen).
   */
  triggerRef?: React.RefObject<HTMLElement>;
};

export const AdminOrgSwitcherPopup = ({ isOpen, onClose, thirdPartyOrganization, contexts, triggerRef }: Props) => {
  const popupRef = React.useRef<HTMLDivElement>(null);
  const prevIsOpenRef = React.useRef(false);

  // Move focus into popup on open; restore to trigger on close (only after it was previously opened).
  React.useEffect(() => {
    if (!isOpen && prevIsOpenRef.current) {
      // Just closed (was open, now closed) → restore focus to trigger
      getFocusableElement(triggerRef?.current ?? null)?.focus();
    }

    if (isOpen) {
      // Just opened → move focus into popup
      getFocusableElement(popupRef.current)?.focus();
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, triggerRef]);

  // Close on outside click. The trigger element (e.g. the profile pill) is treated
  // as "inside" so its own onClick is allowed to toggle state cleanly.
  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inPopup = popupRef.current?.contains(target);
      const inTrigger = triggerRef?.current?.contains(target);
      if (!inPopup && !inTrigger) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Escape closes the popup regardless of where focus currently is.
  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div
      ref={popupRef}
      id="org-switcher-popup"
      role="dialog"
      aria-modal="true"
      aria-label={_t('Organization switcher')}
      css={[styles.popup, !isOpen && styles.popupHidden]}
    >
      <AdminOrgSwitcherContent
        thirdPartyOrganization={thirdPartyOrganization}
        contexts={contexts}
        onItemActivate={onClose}
      />
    </div>
  );
};

export default AdminOrgSwitcherPopup;
